// dbPriceRules.js
// Converts database pricing data to w16 format (same structure as w16_priceRules.js)
// This module fetches from /pricing/all (normalized format) and converts locally

import { authFetch } from "../../../utils/api";
import { SHOW_DB_PRICE_AND_MATCH } from "../../../constants";

// Cache for converted price rules
let priceRulesCache = null;
let cacheTime = 0;
const CACHE_TTL = 60000; // 1 minute cache

/**
 * Fetches pricing data from database and converts to w16 format
 * @returns {Promise<Array>} Price rules in w16 format (same as w16_priceRules.js)
 */
export async function fetchDbPriceRules() {
  const now = Date.now();
  if (priceRulesCache && (now - cacheTime) < CACHE_TTL) {
    return priceRulesCache;
  }

  try {
    // Fetch normalized data from /pricing/all
    const data = await authFetch('/pricing/all');

    if (!data) {
      console.warn("No pricing data returned from /pricing/all");
      return [];
    }

    // Convert to w16 format
    const priceRules = convertToW16Format(data);

    if (SHOW_DB_PRICE_AND_MATCH) {
      // Download converted rules as JSON file (for debugging)
      downloadAsFile('db_priceRules_converted.json', priceRules);

      // Download raw API data for comparison
      downloadAsFile('db_priceRules_raw.json', data);
    }

    priceRulesCache = priceRules;
    cacheTime = now;

    return priceRules;
  } catch (error) {
    console.error("Failed to fetch/convert pricing rules:", error);
    return [];
  }
}

/**
 * Clear the cache (call when admin updates pricing)
 */
export function clearDbPriceRulesCache() {
  priceRulesCache = null;
  cacheTime = 0;
}

/**
 * Convert normalized DB data to w16 format
 * @param {Object} data - Normalized data from /pricing/all
 * @returns {Array} Price rules in w16 format
 */
function convertToW16Format(data) {
  const priceRules = [];

  const { tables, lookups, lookups_2d, rules, formulas } = data;

  // Build table code lookup by id
  const tableCodeById = {};
  tables?.forEach(t => {
    tableCodeById[t.id] = t.code;
  });

  // 1. Convert Price Tables to w16 format: { id: 'ref_xxx', rows: [{min, max, 'Picture': 66.50, ...}] }
  tables?.forEach(table => {
    const w16Rows = [];

    // Build column code lookup (using 'code' field for column name in w16)
    const columnCodeById = {};
    table.columns?.forEach(col => {
      columnCodeById[col.id] = col.code || col.label;  // Fallback to label if code is empty
    });

    // Build rows with cell values
    table.rows?.forEach(row => {
      const rowData = {
        min: parseFloat(row.min_value),
        max: parseFloat(row.max_value)
      };

      // Add cell values keyed by column code
      const cells = row.cells || [];

      cells.forEach(cell => {
        const colCode = columnCodeById[cell.pricing_table_column_id];
        if (colCode) {
          if (cell.value_type === 'formula' && cell.value_formula) {
            // Keep formula as string (e.g., "9.80*square")
            rowData[colCode] = cell.value_formula;
          } else {
            rowData[colCode] = parseFloat(cell.value_numeric) || 0;
          }
        }
      });

      w16Rows.push(rowData);
    });

    priceRules.push({
      id: table.code,
      rows: w16Rows
    });
  });

  // 2. Convert 1D Lookups: { id: 'ref_Textured', Frosted: 30, Sandblast: 30, ... }
  lookups?.forEach(lookup => {
    const lookupRule = { id: lookup.code };
    lookup.entries?.forEach(entry => {
      lookupRule[entry.key_value] = parseFloat(entry.price);
    });
    priceRules.push(lookupRule);
  });

  // 3. Convert 2D Lookups: { id: 'ref_GlassEnergy', '3mm': {'LowE 272': 0, ...}, ... }
  lookups_2d?.forEach(lookup => {
    const lookupRule = { id: lookup.code };

    // Build column key lookup
    const colKeyById = {};
    lookup.columns?.forEach(col => {
      colKeyById[col.id] = col.key_value;
    });

    // Build nested structure by row
    lookup.rows?.forEach(row => {
      const rowKey = row.key_value;
      lookupRule[rowKey] = {};

      row.cells?.forEach(cell => {
        const colKey = colKeyById[cell.pricing_lookup_2d_column_id];
        if (colKey) {
          lookupRule[rowKey][colKey] = parseFloat(cell.price);
        }
      });
    });

    priceRules.push(lookupRule);
  });

  // 4. Convert Pricing Rules (w1, w2, top1, etc.) - build from pricing_rule_conditions
  rules?.forEach(rule => {
    // Add prefix only if not already present
    const valueExpr = rule.value_expr.startsWith('doorData.')
      ? rule.value_expr
      : `doorData.frameData.${rule.value_expr}`;
    const columnExpr = rule.column_expr.startsWith('doorData.')
      ? rule.column_expr
      : `doorData.frameData.${rule.column_expr}`;

    const tableObj = { valueExpr, columnExpr };

    // Build id1/condition1, id2/condition2, etc. from rule.conditions (sorted by priority)
    const sortedConditions = (rule.conditions || []).slice().sort((a, b) => a.priority - b.priority);
    sortedConditions.forEach((cond, idx) => {
      const num = idx + 1;
      tableObj[`id${num}`] = tableCodeById[cond.pricing_table_id];
      // Convert JSON condition to JavaScript expression for eval()
      tableObj[`condition${num}`] = convertConditionJsonToJs(cond.conditions_json);
    });

    const priceRule = {
      id: rule.code,
      active: rule.active,
      applies_to: rule.applies_to || 'all',
      table: tableObj
    };

    // Add surcharge if present
    if (rule.surcharge) {
      const sc = rule.surcharge;
      const surchargeObj = {
        condition: sc.condition || 'result > 0'
      };

      if (sc.type === 'fixed') {
        surchargeObj.add = parseFloat(sc.value) || 0;
      } else if (sc.type === '1d') {
        // Find lookup code from ID
        const lookupTable = lookups?.find(l => l.id === sc.lookupId);
        const lookupCode = lookupTable?.code || `lookup_${sc.lookupId}`;
        surchargeObj.add = [lookupCode, sc.key1Attr];
      } else if (sc.type === '2d') {
        // Find lookup code from ID
        const lookupTable = lookups_2d?.find(l => l.id === sc.lookupId);
        const lookupCode = lookupTable?.code || `lookup_${sc.lookupId}`;
        surchargeObj.add = {
          id: lookupCode,
          key1: sc.key1Attr,
          key2: sc.key2Attr
        };
      }

      priceRule.surcharge = surchargeObj;
    }

    priceRules.push(priceRule);
  });

  // 5. Convert Formula rules (grills, textured, etc.)
  formulas?.forEach(f => {
    // Build formula with parameter aliases replaced
    let formulaExpr = f.formula || '';
    const parameters = f.parameters || [];

    // Replace aliases with actual attribute paths in the formula
    // e.g., "H" -> "Number(doorData.grills.glassDividersHorizontal1 || 0)"
    parameters.forEach(param => {
      if (param.alias && param.attr) {
        let attrPath = param.attr;

        // If attr already has Number() wrapper, extract the inner path
        // e.g., "Number(doorData.grills.glassDividersHorizontal1 || 0)" -> "doorData.grills.glassDividersHorizontal1"
        const numberMatch = attrPath.match(/^Number\(([^|]+?)(?:\s*\|\|\s*\d+)?\)$/);
        if (numberMatch) {
          attrPath = numberMatch[1].trim();
        }

        // Now wrap with Number() (only once)
        const replacement = `Number(${attrPath} || 0)`;

        // Replace whole word only (not partial matches)
        const regex = new RegExp(`\\b${param.alias}\\b`, 'g');
        const beforeReplace = formulaExpr;
        formulaExpr = formulaExpr.replace(regex, replacement);

        // Warn if alias wasn't found in formula
        if (beforeReplace === formulaExpr) {
          console.warn(`⚠️ Formula "${f.code}": alias "${param.alias}" not found in formula. Check for typos.`);
        }
      }
    });

    // Handle price lookups - replace <<alias>> with lookup references
    // e.g., "totalSquare * <<price1>>" -> "totalSquare * lookupPrice('ref_GlassEnergy', doorData.glass.glassThickness1, doorData.glass.glassEnergy1.value)"
    const priceLookups = f.priceLookups || [];
    const lookupRefs = []; // Store lookup definitions for the formula rule
    priceLookups.forEach(lookup => {
      if (lookup.alias && lookup.lookupId) {
        // Find lookup table code from ID (lookups and lookups_2d are from the data parameter)
        const lookupTable = lookup.type === '2d'
          ? lookups_2d?.find(l => l.id === lookup.lookupId)
          : lookups?.find(l => l.id === lookup.lookupId);

        const lookupCode = lookupTable?.code || `lookup_${lookup.lookupId}`;

        // Build lookup reference object
        const lookupRef = {
          alias: lookup.alias,
          type: lookup.type,
          id: lookupCode,
          key1: lookup.key1Attr
        };
        if (lookup.type === '2d' && lookup.key2Attr) {
          lookupRef.key2 = lookup.key2Attr;
        }
        lookupRefs.push(lookupRef);

        // Replace <<alias>> in formula with lookup placeholder
        const aliasPattern = new RegExp(`<<${lookup.alias}>>`, 'g');
        formulaExpr = formulaExpr.replace(aliasPattern, `__LOOKUP_${lookup.alias}__`);
      }
    });

    const formulaRule = {
      id: f.code,
      formula: formulaExpr,
      unitPrice: parseFloat(f.unit_price) || 1,
      minValue: parseFloat(f.min_value) || 0,
      applies_to: f.applies_to || 'all'
    };

    // Add lookup references if any
    if (lookupRefs.length > 0) {
      lookupRefs.forEach((ref, idx) => {
        const num = idx + 1;
        if (ref.type === '2d') {
          formulaRule[`price${num}`] = {
            id: ref.id,
            key1: ref.key1,
            key2: ref.key2
          };
        } else {
          formulaRule[`price${num}`] = [ref.id, ref.key1];
        }
        // Replace placeholder with reference in formula
        formulaExpr = formulaExpr.replace(`__LOOKUP_${ref.alias}__`, `<<price${num}>>`);
      });
      formulaRule.formula = formulaExpr;
    }

    // Build condition from conditions array (convert JSON to JS expression)
    const conditions = f.conditions || [];
    if (conditions.length > 0) {
      formulaRule.condition = convertConditionsToJs(conditions);
    } else if (f.condition_expr) {
      // Fallback to legacy condition_expr
      formulaRule.condition = f.condition_expr;
    }

    // Build multiplicator object from multiplicators array
    const multiplicators = f.multiplicators || [];
    if (multiplicators.length > 0) {
      const multiplicator = {};
      multiplicators.forEach((mult, idx) => {
        const num = idx + 1;
        // Convert multiplicator conditions to JS expression
        const condExpr = convertConditionsToJs(mult.conditions || []);
        multiplicator[`condition${num}`] = condExpr;
        multiplicator[`value${num}`] = parseFloat(mult.multiplier) || 1;
      });
      formulaRule.multiplicator = multiplicator;
    }

    priceRules.push(formulaRule);
  });

  // 6. Convert Surcharges (optional - if needed in w16 format)
  // surcharges?.forEach(s => { ... });

  return priceRules;
}

/**
 * Convert JSON condition array to JavaScript expression for eval()
 * Input: '[{"attr": "doorData.finish.interiorColor", "op": "=", "value": "white"}, ...]'
 * Output: "doorData.finish.interiorColor.label === 'White' && doorData.finish.exteriorColor.label === 'White'"
 */
function convertConditionJsonToJs(conditionsJson) {
  if (!conditionsJson) return 'true';

  let conditions;
  try {
    conditions = typeof conditionsJson === 'string' ? JSON.parse(conditionsJson) : conditionsJson;
  } catch (e) {
    console.warn('Failed to parse conditions_json:', conditionsJson);
    return 'true';
  }

  return convertConditionsToJs(conditions);
}

/**
 * Convert conditions array to JavaScript expression for eval()
 * Input: [{ attr: "doorData.finish.interiorColor", op: "=", value: "white" }, ...]
 * Output: "doorData.finish.interiorColor.label === 'White' && ..."
 */
function convertConditionsToJs(conditions) {
  if (!Array.isArray(conditions) || conditions.length === 0) return 'true';

  const parts = conditions.map(cond => {
    let attr = cond.attr || '';
    let value = cond.value || '';
    const op = cond.op || '=';

    // Check if this is a color-related attribute (add .label if needed)
    const isColorAttr = attr.toLowerCase().includes('color') || attr.toLowerCase().includes('finish');
    if (isColorAttr && !attr.endsWith('.label') && !attr.endsWith('.value')) {
      attr = attr + '.label';
    }

    // Capitalize value for color comparisons: 'white' -> 'White'
    if (isColorAttr && typeof value === 'string' && value.length > 0) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }

    // Map operators
    let jsOp;
    switch (op) {
      case '=': jsOp = '==='; break;
      case '!=': jsOp = '!=='; break;
      case '>': jsOp = '>'; break;
      case '<': jsOp = '<'; break;
      case '>=': jsOp = '>='; break;
      case '<=': jsOp = '<='; break;
      default: jsOp = '===';
    }

    return `${attr} ${jsOp} '${value}'`;
  });

  return parts.join(' && ');
}

/**
 * Helper to download data as a JSON file in the browser
 */
function downloadAsFile(filename, data) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Default export for compatibility with existing code
export default fetchDbPriceRules;
